import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import useSchool, { BRAND_COLOR_FALLBACK } from '@/hooks/useSchool';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowLeft, CheckCircle2, RefreshCw, Trash2, Pencil, Send, Loader2, X, Plus,
} from 'lucide-react';

const MAX_QUESTIONS = 25;

export default function SchoolTeacherAssessmentReview() {
  const navigate = useNavigate();
  const { aid } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyQid, setBusyQid] = useState(null);
  const [editingQid, setEditingQid] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [me, setMe] = useState(null);
  const school = useSchool(me?.school_id);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/teacher/me/assessments/${aid}`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load draft');
      navigate('/school-teacher/assessments');
    } finally {
      setLoading(false);
    }
  }, [aid, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const meRes = await api.get('/auth/me');
        setMe(meRes.data);
      } catch {/* */}
    })();
    load();
  }, [load]);

  const brandColor = school?.brand_color || BRAND_COLOR_FALLBACK;
  const a = data?.assessment;
  const questions = data?.questions || [];

  const approvedCount = useMemo(
    () => questions.filter((q) => q.approved_by_teacher).length,
    [questions]
  );
  const allApproved = questions.length > 0 && approvedCount === questions.length;

  const onApprove = async (qid) => {
    setBusyQid(qid);
    try {
      await api.post(`/teacher/me/assessments/${aid}/questions/${qid}/approve`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    } finally { setBusyQid(null); }
  };

  const onRegenerate = async (qid) => {
    setBusyQid(qid);
    try {
      await api.post(`/teacher/me/assessments/${aid}/questions/${qid}/regenerate`);
      toast.success('Regenerated — review again');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Regenerate failed');
    } finally { setBusyQid(null); }
  };

  const onDelete = async (qid) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this question? This cannot be undone.')) return;
    setBusyQid(qid);
    try {
      await api.delete(`/teacher/me/assessments/${aid}/questions/${qid}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally { setBusyQid(null); }
  };

  const onSaveEdit = async (qid, payload) => {
    setBusyQid(qid);
    try {
      await api.patch(`/teacher/me/assessments/${aid}/questions/${qid}`, payload);
      setEditingQid(null);
      await load();
      toast.success('Saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusyQid(null); }
  };

  const onPublish = async () => {
    setPublishing(true);
    try {
      await api.post(`/teacher/me/assessments/${aid}/publish`);
      toast.success('Published — students will be notified by email.');
      navigate(`/school-teacher/assessments/${aid}/status`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Publish failed');
    } finally { setPublishing(false); }
  };

  const onAddManual = async (payload) => {
    setAddBusy(true);
    try {
      await api.post(`/teacher/me/assessments/${aid}/questions`, payload);
      toast.success('Question added');
      setAddOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add question');
    } finally { setAddBusy(false); }
  };

  const atCap = questions.length >= MAX_QUESTIONS;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-indigo-600" />
      </div>
    );
  }
  if (!a) return null;

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 pb-32"
      data-testid="school-teacher-assessment-review"
    >
      <header
        className="border-b bg-white/90 backdrop-blur sticky top-0 z-10"
        style={{ borderTopWidth: 4, borderTopColor: brandColor }}
      >
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/school-teacher/assessments')} data-testid="back-btn">
            <ArrowLeft size={16} className="mr-1.5" /> Back
          </Button>
          <div className="flex-1">
            <h1 className="font-bold text-slate-900" data-testid="review-title">
              {a.title}
            </h1>
            <p className="text-xs text-slate-500">
              {data.school_class?.name} · Grade {data.school_class?.grade}{data.school_class?.section ? `-${data.school_class.section}` : ''} · {data.subject?.name}
            </p>
          </div>
          <Badge variant="outline" className={a.status === 'draft' ? 'bg-amber-50 text-amber-700 border-amber-300' : 'bg-emerald-50 text-emerald-700 border-emerald-300'}>
            {a.status === 'draft' ? 'Draft' : a.status}
          </Badge>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-4">
        <div className="bg-white border rounded-xl p-4 text-sm text-slate-600 flex flex-wrap gap-x-4 gap-y-1" data-testid="review-meta">
          <span>📌 Topic: <strong className="text-slate-800">{a.topic_seed}</strong></span>
          <span>🗓 Due: <strong className="text-slate-800">{a.due_at ? new Date(a.due_at).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</strong></span>
          <span>🏁 Mode: <strong className="text-slate-800">{a.release_mode === 'auto' ? 'Auto-release' : 'Hold-back'}</strong></span>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="approval-progress">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
            Questions · {approvedCount} of {questions.length} approved
          </h2>
          {a.status === 'draft' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddOpen(true)}
              disabled={atCap}
              title={atCap ? `Maximum ${MAX_QUESTIONS} questions reached` : 'Add a question manually'}
              data-testid="add-manual-question-btn"
            >
              <Plus size={14} className="mr-1.5" />
              Add manually
            </Button>
          )}
        </div>

        {questions.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-8 text-center text-sm text-slate-600">
            No questions were generated. Try creating again with a more specific topic seed.
          </div>
        ) : (
          questions.map((q, idx) => (
            <QuestionCard
              key={q.question_id}
              question={q}
              index={idx + 1}
              busy={busyQid === q.question_id}
              isEditing={editingQid === q.question_id}
              status={a.status}
              onEdit={() => setEditingQid(q.question_id)}
              onCancelEdit={() => setEditingQid(null)}
              onSaveEdit={onSaveEdit}
              onApprove={() => onApprove(q.question_id)}
              onRegenerate={() => onRegenerate(q.question_id)}
              onDelete={() => onDelete(q.question_id)}
            />
          ))
        )}
      </main>

      {a.status === 'draft' && (
        <div
          className="fixed bottom-0 inset-x-0 border-t bg-white/95 backdrop-blur"
          data-testid="publish-bar"
        >
          <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
            <div className="text-sm text-slate-600">
              {allApproved ? (
                <span className="text-emerald-700 font-medium">All questions approved ✓</span>
              ) : (
                <span>{questions.length - approvedCount} more to approve before publish</span>
              )}
            </div>
            <Button
              disabled={!allApproved || publishing}
              onClick={onPublish}
              style={{ backgroundColor: brandColor }}
              className="text-white"
              data-testid="publish-btn"
            >
              {publishing ? <Loader2 size={16} className="animate-spin mr-2" /> : <Send size={16} className="mr-2" />}
              Publish to class
            </Button>
          </div>
        </div>
      )}

      <AddManualQuestionDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onSubmit={onAddManual}
        busy={addBusy}
      />
    </div>
  );
}

function QuestionCard({
  question, index, busy, isEditing, status,
  onEdit, onCancelEdit, onSaveEdit, onApprove, onRegenerate, onDelete,
}) {
  const [prompt, setPrompt] = useState(question.prompt);
  const [options, setOptions] = useState(question.options);
  const [correct, setCorrect] = useState(question.correct_option_id);
  const [reasoning, setReasoning] = useState(question.correct_reasoning);

  useEffect(() => {
    setPrompt(question.prompt);
    setOptions(question.options);
    setCorrect(question.correct_option_id);
    setReasoning(question.correct_reasoning);
  }, [question.question_id, question.prompt, question.options, question.correct_option_id, question.correct_reasoning]);

  const editable = status === 'draft';

  const setOptionText = (id, text) => {
    setOptions((prev) => prev.map((o) => (o.id === id ? { ...o, text } : o)));
  };

  return (
    <div
      className={`bg-white border rounded-xl p-5 shadow-sm ${question.approved_by_teacher ? 'border-emerald-300' : 'border-slate-200'}`}
      data-testid={`question-card-${question.question_id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-slate-700">Q{index}</span>
          {question.approved_by_teacher && (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300">
              Approved
            </Badge>
          )}
          {question.source === 'manual' && (
            <Badge
              variant="outline"
              className="bg-sky-50 text-sky-700 border-sky-300"
              data-testid={`manual-badge-${question.question_id}`}
            >
              Manually added
            </Badge>
          )}
        </div>
      </div>

      {isEditing ? (
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Prompt</Label>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={3}
              data-testid={`edit-prompt-${question.question_id}`}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Options (one correct)</Label>
            {options.map((opt) => (
              <div key={opt.id} className="flex items-center gap-2">
                <input
                  type="radio"
                  name={`correct-${question.question_id}`}
                  checked={correct === opt.id}
                  onChange={() => setCorrect(opt.id)}
                  data-testid={`edit-correct-${question.question_id}-${opt.id}`}
                />
                <span className="font-mono text-slate-500 text-sm w-5">{opt.id}.</span>
                <Input
                  value={opt.text}
                  onChange={(e) => setOptionText(opt.id, e.target.value)}
                  data-testid={`edit-option-${question.question_id}-${opt.id}`}
                />
              </div>
            ))}
          </div>
          <div>
            <Label className="text-xs">Correct reasoning</Label>
            <Textarea
              value={reasoning}
              onChange={(e) => setReasoning(e.target.value)}
              rows={3}
              data-testid={`edit-reasoning-${question.question_id}`}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="ghost" size="sm" onClick={onCancelEdit} disabled={busy} data-testid={`cancel-edit-${question.question_id}`}>
              <X size={14} className="mr-1" /> Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => onSaveEdit(question.question_id, { prompt, options, correct_option_id: correct, correct_reasoning: reasoning })}
              disabled={busy}
              data-testid={`save-edit-${question.question_id}`}
            >
              {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <CheckCircle2 size={14} className="mr-1" />}
              Save
            </Button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-slate-900 font-medium mb-3" data-testid={`q-prompt-${question.question_id}`}>
            {question.prompt}
          </p>
          <div className="space-y-2 mb-3">
            {question.options.map((opt) => {
              const isCorrect = opt.id === question.correct_option_id;
              return (
                <div
                  key={opt.id}
                  className={`flex items-start gap-2 px-3 py-2 rounded-lg text-sm ${isCorrect ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50 border border-slate-100'}`}
                  data-testid={`q-option-${question.question_id}-${opt.id}`}
                >
                  <span className={`font-mono ${isCorrect ? 'text-emerald-700 font-bold' : 'text-slate-500'}`}>{opt.id}.</span>
                  <span className={isCorrect ? 'text-emerald-900' : 'text-slate-700'}>{opt.text}</span>
                  {isCorrect && <Badge variant="outline" className="ml-auto bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px]">Correct</Badge>}
                </div>
              );
            })}
          </div>
          <div className="bg-indigo-50/60 border border-indigo-100 rounded-lg p-3 text-sm text-slate-700 mb-3" data-testid={`q-reasoning-${question.question_id}`}>
            <strong className="text-indigo-700">Reasoning:</strong> {question.correct_reasoning}
          </div>
          {editable && (
            <div className="flex flex-wrap gap-2 justify-end pt-2 border-t">
              <Button size="sm" variant="ghost" onClick={onEdit} disabled={busy} data-testid={`edit-btn-${question.question_id}`}>
                <Pencil size={14} className="mr-1" /> Edit
              </Button>
              <Button size="sm" variant="ghost" onClick={onRegenerate} disabled={busy} data-testid={`regenerate-btn-${question.question_id}`}>
                {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <RefreshCw size={14} className="mr-1" />}
                Regenerate
              </Button>
              <Button size="sm" variant="ghost" onClick={onDelete} disabled={busy} className="text-red-600 hover:text-red-700" data-testid={`delete-btn-${question.question_id}`}>
                <Trash2 size={14} className="mr-1" /> Delete
              </Button>
              {!question.approved_by_teacher && (
                <Button size="sm" onClick={onApprove} disabled={busy} data-testid={`approve-btn-${question.question_id}`}>
                  {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <CheckCircle2 size={14} className="mr-1" />}
                  Approve
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function AddManualQuestionDialog({ open, onOpenChange, onSubmit, busy }) {
  const [prompt, setPrompt] = useState('');
  const [optA, setOptA] = useState('');
  const [optB, setOptB] = useState('');
  const [optC, setOptC] = useState('');
  const [optD, setOptD] = useState('');
  const [correct, setCorrect] = useState('A');
  const [reasoning, setReasoning] = useState('');

  // Reset on open
  useEffect(() => {
    if (open) {
      setPrompt('');
      setOptA('');
      setOptB('');
      setOptC('');
      setOptD('');
      setCorrect('A');
      setReasoning('');
    }
  }, [open]);

  const promptValid = prompt.trim().length >= 5 && prompt.trim().length <= 600;
  const optsValid = [optA, optB, optC, optD].every((t) => t.trim().length >= 1 && t.trim().length <= 200);
  const reasonValid = reasoning.trim().length >= 5 && reasoning.trim().length <= 1200;
  const canSubmit = promptValid && optsValid && reasonValid && !busy;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      prompt: prompt.trim(),
      options: [
        { id: 'A', text: optA.trim() },
        { id: 'B', text: optB.trim() },
        { id: 'C', text: optC.trim() },
        { id: 'D', text: optD.trim() },
      ],
      correct_option_id: correct,
      correct_reasoning: reasoning.trim(),
    });
  };

  const optionRow = (id, value, setValue) => (
    <div className="flex items-center gap-2" key={id}>
      <input
        type="radio"
        name="manual-q-correct"
        checked={correct === id}
        onChange={() => setCorrect(id)}
        data-testid={`manual-q-correct-${id}`}
        aria-label={`Mark option ${id} as correct`}
      />
      <span className="font-mono text-slate-500 text-sm w-5">{id}.</span>
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={`Option ${id}`}
        maxLength={200}
        data-testid={`manual-q-opt-${id}`}
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="manual-q-dialog">
        <DialogHeader>
          <DialogTitle>Add a question manually</DialogTitle>
          <DialogDescription>
            This question will be saved as auto-approved. You can still edit, regenerate, or delete it before publishing.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Prompt</Label>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={3}
              maxLength={600}
              placeholder="What concept do you want to test?"
              data-testid="manual-q-prompt"
            />
            <div className="text-[11px] text-slate-400 text-right mt-0.5">{prompt.length}/600</div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Options (select the correct one)</Label>
            {optionRow('A', optA, setOptA)}
            {optionRow('B', optB, setOptB)}
            {optionRow('C', optC, setOptC)}
            {optionRow('D', optD, setOptD)}
          </div>

          <div>
            <Label className="text-xs">Correct reasoning</Label>
            <Textarea
              value={reasoning}
              onChange={(e) => setReasoning(e.target.value)}
              rows={3}
              maxLength={1200}
              placeholder="Explain why the chosen option is correct."
              data-testid="manual-q-reasoning"
            />
            <div className="text-[11px] text-slate-400 text-right mt-0.5">{reasoning.length}/1200</div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="manual-q-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="manual-q-submit"
          >
            {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <CheckCircle2 size={14} className="mr-1" />}
            Save question
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

